import { useState, useRef, useEffect } from 'react';

interface OTPInputProps {
  length?: number;
  onComplete: (otp: string) => void;
  onResend: () => void;
  isLoading?: boolean;
  isResending?: boolean;
  error?: string;
  remainingAttempts?: number;
  nextAllowedAt?: string;
  className?: string;
}

export function OTPInput({ 
  length = 6,
  onComplete,
  onResend,
  isLoading = false,
  isResending = false,
  error,
  remainingAttempts,
  nextAllowedAt,
  className = ''
}: OTPInputProps) {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // 재전송 쿨다운 타이머
  useEffect(() => {
    if (nextAllowedAt) {
      const nextTime = new Date(nextAllowedAt).getTime();
      const updateCooldown = () => {
        const now = Date.now();
        const remaining = Math.max(0, Math.ceil((nextTime - now) / 1000));
        setResendCooldown(remaining);
        
        if (remaining > 0) {
          setTimeout(updateCooldown, 1000);
        }
      };
      updateCooldown();
    }
  }, [nextAllowedAt]);

  const handleChange = (index: number, value: string) => {
    // 숫자만 허용
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // 마지막 한 자리만
    setOtp(newOtp);

    // 자동으로 다음 입력창으로 이동
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // OTP가 완성되면 콜백 호출
    const otpString = newOtp.join('');
    if (otpString.length === length) {
      onComplete(otpString);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // 현재 칸이 비어있고 Backspace를 누르면 이전 칸으로 이동
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, ''); // 숫자만 추출
    
    if (pastedData.length >= length) {
      const newOtp = pastedData.slice(0, length).split('');
      setOtp(newOtp);
      onComplete(newOtp.join(''));
      
      // 마지막 입력창에 포커스
      inputRefs.current[length - 1]?.focus();
    }
  };

  const clearOtp = () => {
    setOtp(Array(length).fill(''));
    inputRefs.current[0]?.focus();
  };

  const canResend = resendCooldown === 0 && !isResending && (remainingAttempts ?? 1) > 0;

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`otp-input-container ${className}`}>
      {/* OTP 입력 필드 */}
      <div className="flex justify-center gap-2 mb-6">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={isLoading}
            className={`
              w-8 h-10 sm:w-10 sm:h-12 text-center text-xl font-bold
              border-2 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-[#4CD964] focus:border-[#4CD964]
              transition-all duration-200
              ${digit ? 'border-[#4CD964] bg-green-50' : 'border-gray-300 bg-white'}
              ${error ? 'border-red-500 bg-red-50' : ''}
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-text'}
              hover:border-[#4CD964]
            `}
            placeholder="0"
          />
        ))}
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="text-red-500 text-sm text-center mb-4 bg-red-50 p-3 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {/* 재전송 버튼 및 정보 */}
      <div className="text-center space-y-3">
        <div className="text-sm text-gray-600">
          인증번호를 받지 못하셨나요?
        </div>
        
        <button
          onClick={onResend}
          disabled={!canResend}
          className={`
            px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200
            ${canResend 
              ? 'bg-[#4CD964] text-white hover:bg-[#45B85C] active:scale-95' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }
          `}
        >
          {isResending ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              발송 중...
            </div>
          ) : resendCooldown > 0 ? (
            `재전송 (${formatTime(resendCooldown)})`
          ) : (
            '인증번호 재전송'
          )}
        </button>

        {/* 재전송 제한 정보 */}
        {typeof remainingAttempts === 'number' && (
          <div className="text-xs text-gray-500">
            {remainingAttempts > 0 ? (
              <>남은 재전송 횟수: <span className="font-medium text-[#4CD964]">{remainingAttempts}회</span></>
            ) : (
              <span className="text-red-500">재전송 횟수를 모두 사용했습니다</span>
            )}
          </div>
        )}

        {/* 도움말 */}
        <div className="text-xs text-gray-400 space-y-1">
          <div>• 인증번호는 10분간 유효합니다</div>
          <div>• 1시간 동안 최대 3회 요청 가능합니다</div>
          <div>• 5회 입력 실패 시 새로운 인증번호를 요청해주세요</div>
        </div>

        {/* 초기화 버튼 */}
        {otp.some(digit => digit) && (
          <button
            onClick={clearOtp}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            입력 초기화
          </button>
        )}
      </div>
    </div>
  );
}